import { useState, useMemo } from "react";
import { Camera, Eye, Star, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { useGradePricing } from "@/hooks/useGradePricing";
import { useTranslation } from "react-i18next";

interface QualityGradingContentProps {
  onComplete: (grading: {
    aestheticScore: number;
    functionalityScore: number;
    marketabilityScore: number;
    photo: File | null;
    selectedGrade: string;
    notes: string;
  }) => void;
}

const defaultPricing = { A: 75, B: 65, C: 50 };

export function QualityGradingContent({ onComplete }: QualityGradingContentProps) {
  const { t } = useTranslation("refurb");

  const { id: companyId } = useCurrentCompany();
  const { data: gradePricingData } = useGradePricing(companyId);
  const gradePricing = gradePricingData?.effective;

  const pricing = gradePricing || defaultPricing;

  const gradesMeta = [
    { id: "A", label: t("grading.premium"), description: t("grading.premiumDesc"), scoreRange: "4.5 - 5.0", minScore: 4.5, hasAI: true, pricingLabel: t("grading.premiumPricing") },
    { id: "B", label: t("grading.good"), description: t("grading.goodDesc"), scoreRange: "3.0 - 4.4", minScore: 3.0, hasAI: false, pricingLabel: t("grading.standardPricing") },
    { id: "C", label: t("grading.fair"), description: t("grading.fairDesc"), scoreRange: "1.5 - 2.9", minScore: 1.5, hasAI: false, pricingLabel: t("grading.economyPricing") },
  ];

  const grades = gradesMeta.map((g) => ({
    ...g,
    pricingNote: t("grading.pricingNote", { label: g.pricingLabel, pct: pricing[g.id as keyof typeof pricing] }),
  }));

  const [photo, setPhoto] = useState<File | null>(null);
  const [aestheticScore, setAestheticScore] = useState(3);
  const [functionalityScore, setFunctionalityScore] = useState(2);
  const [marketabilityScore, setMarketabilityScore] = useState(3);
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
  const [gradingNotes, setGradingNotes] = useState("");

  const overallScore = useMemo(() => {
    const total = aestheticScore + functionalityScore + marketabilityScore;
    return (total / 3).toFixed(1);
  }, [aestheticScore, functionalityScore, marketabilityScore]);

  const recommendedGrade = useMemo(() => {
    const score = parseFloat(overallScore);
    if (score >= 4.5) return "A";
    if (score >= 3.0) return "B";
    return "C";
  }, [overallScore]);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setPhoto(e.target.files[0]);
  };

  const handleComplete = () => {
    if (selectedGrade) {
      onComplete({ aestheticScore, functionalityScore, marketabilityScore, photo, selectedGrade, notes: gradingNotes });
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-primary/20 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-5 h-5 text-primary" />
          <h4 className="font-semibold text-foreground">{t("grading.cvAnalysis")}</h4>
          <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
            <Sparkles className="w-3 h-3" />
            YOLO v7
          </span>
        </div>

        <p className="text-sm font-medium text-foreground mb-3">{t("grading.productPhoto")}</p>

        <label className="block">
          <div className="border-2 border-dashed border-primary/30 rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
            {photo ? (
              <div className="space-y-2">
                <img src={URL.createObjectURL(photo)} alt="Product" className="max-h-40 mx-auto rounded-lg object-contain" />
                <p className="text-sm text-muted-foreground">{t("grading.changePhoto")}</p>
              </div>
            ) : (
              <>
                <Camera className="w-10 h-10 mx-auto text-primary/50 mb-3" />
                <p className="font-medium text-primary mb-1">{t("grading.uploadImage")}</p>
                <p className="text-sm text-muted-foreground">{t("grading.uploadDesc")}</p>
              </>
            )}
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
        </label>
      </div>

      <div className="bg-card rounded-xl border border-primary/20 p-5">
        <div className="flex items-center gap-2 mb-5">
          <Star className="w-5 h-5 text-primary" />
          <h4 className="font-semibold text-primary">{t("grading.manualAssessment")}</h4>
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">{t("grading.aesthetic", { score: aestheticScore })}</span>
            </div>
            <Slider value={[aestheticScore]} onValueChange={(v) => setAestheticScore(v[0])} max={5} step={1} className="mb-1" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t("grading.poor")}</span><span>{t("grading.excellent")}</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">{t("grading.functionality", { score: functionalityScore })}</span>
            </div>
            <Slider value={[functionalityScore]} onValueChange={(v) => setFunctionalityScore(v[0])} max={5} step={1} className="mb-1" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t("grading.issues")}</span><span>{t("grading.perfect")}</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">{t("grading.marketability", { score: marketabilityScore })}</span>
            </div>
            <Slider value={[marketabilityScore]} onValueChange={(v) => setMarketabilityScore(v[0])} max={5} step={1} className="mb-1" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t("grading.limited")}</span><span>{t("grading.highDemand")}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 rounded-xl p-5 text-center">
        <p className="text-sm text-muted-foreground mb-1">{t("grading.overallScore")}</p>
        <p className="text-4xl font-bold text-foreground mb-2">{overallScore}/5.0</p>
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
          <Sparkles className="w-3 h-3" />
          {t("grading.recommendedGrade", { grade: recommendedGrade })}
        </span>
      </div>

      <div className="space-y-3">
        {grades.map((grade) => {
          const isSelected = selectedGrade === grade.id;
          const isRecommended = recommendedGrade === grade.id;
          return (
            <button
              key={grade.id}
              onClick={() => setSelectedGrade(grade.id)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {t("grading.gradeFull", { id: grade.id, label: grade.label })}
                  </span>
                  {grade.hasAI && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                      <Sparkles className="w-3 h-3" />
                      AI
                    </span>
                  )}
                  {isRecommended && (
                    <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs font-medium rounded-full">{t("grading.recommended")}</span>
                  )}
                </div>
                {isSelected && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{grade.description}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("grading.scoreRange", { range: grade.scoreRange, pricing: grade.pricingNote })}
              </p>
            </button>
          );
        })}
      </div>

      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">{t("grading.notesLabel")}</label>
        <Textarea
          value={gradingNotes}
          onChange={(e) => setGradingNotes(e.target.value)}
          placeholder={t("grading.notesPlaceholder")}
          className="min-h-[80px]"
        />
      </div>

      <Button onClick={handleComplete} disabled={!selectedGrade} className="w-full gap-2 bg-primary hover:bg-primary/90">
        <Star className="w-4 h-4" />
        {t("grading.assignGrade", { grade: selectedGrade || "?" })}
      </Button>
    </div>
  );
}
